import Grid from '@mui/material/Grid';
import { v4 as uuidv4 } from 'uuid';
import { Box, Divider, IconButton, Skeleton, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled as materialStyled } from '@mui/material/styles';
import FavouriteSvg from '../../../assets/img/favourite-new.svg';
import FavouriteBold from '../../../assets/img/favourite-red-bold.svg';
import PencilEditSvg from '../../../assets/img/edit-icon-new.svg';
import PencilEditDisabledSvg from '../../../assets/img/edit-icon-new-disabled.svg';
import CustomButton from '../CustomButton/CustomButton';
import { theme } from '../../../theme/theme';
import { Field } from '../../../pages/MyGemVault/MyGemVault';
import { GemColor } from '../../../utils/enums/gem-color.enum';
import { colors } from '../FilterComponent/GemFilterModal/Colors';
import PorolisGemBankSvg from '../../../assets/img/bank-lite.svg';
import PorolisGemBankSvgBold from '../../../assets/img/bank-bold-red.svg';

const CustomGridContainer = materialStyled(Grid)({
  borderRadius: '8px',
  border: '0.5px solid #E1E1E1',
  background: '#FFFFFF',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.05)',
});

export interface IGridTileProps {
  gem: any;
  handleClickFullDetailButton: () => void;
  columns: string[];
  columnValues: string[];
  handleFavourite: () => void;
  handleEditGem?: () => void;
  handleTogglePublic: () => void;
  topRighField?: Field;
  isGemBank?: boolean;
  showPublicIcon?: boolean;
}

const renderColorDot = (value: any) => {
  const colorMatch = colors.find((color) => color.name?.toLowerCase() === value?.toLowerCase());
  return colorMatch ? (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: colorMatch.code,
        marginRight: '8px',
        verticalAlign: 'middle',
        border: colorMatch.name === GemColor.White ? `1px solid ${theme.palette.common.white}` : 'none',
      }}
    />
  ) : null;
};

const GridTile = ({
  gem,
  handleClickFullDetailButton,
  columnValues,
  columns,
  handleFavourite,
  handleEditGem,
  handleTogglePublic,
  topRighField,
  isGemBank,
  showPublicIcon,
}: IGridTileProps) => (
  <CustomGridContainer container overflow="auto">
    <Grid
      item
      padding={{ xs: '80px', sm: '12px', md: '12px' }}
      display="flex"
      justifyContent="center"
      alignItems="center"
      xs={12}
      sm={12}
      minHeight="250px"
    >
      {gem.appearanceImageView}
    </Grid>
    <Grid item xs={12} mb={2} padding={0}>
      <Divider />
    </Grid>
    <Grid paddingX="12px" container justifyContent="space-between" mb="16px">
      <Typography variant="textH5" color="black.dark">
        {gem.species} {gem.color} {gem.shape} {gem.weight}ct
      </Typography>
    </Grid>

    <Grid padding="12px" container display="flex" justifyContent="space-between">
      <Grid item xs={6} md={4}>
        <Typography
          variant="textBaseLg500"
          color="black.dark"
          sx={{
            display: 'block',
            whiteSpace: 'nowrap',
          }}
        >
          {gem.systemId || 'N/A'}
        </Typography>
        <Typography sx={{ color: theme.palette.shades.lightDark }} variant="textXS">
          GemID
        </Typography>
      </Grid>
      {topRighField && isGemBank ? (
        <Grid item xs={6} md={4}>
          <Tooltip title={gem.dimensions || 'N/A'}>
            <Typography
              variant="textBaseLg500"
              color="black.dark"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {gem.dimensions || 'N/A'}
            </Typography>
          </Tooltip>
          <Typography sx={{ color: theme.palette.shades.lightDark }} variant="textXS">
            {topRighField?.column}
          </Typography>
        </Grid>
      ) : (
        <Grid item xs={6} md={4}>
          <Tooltip title={gem?.uginId || 'N/A'}>
            <Typography
              variant="textBaseLg500"
              color="black.dark"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {topRighField && (gem?.uginId || 'N/A')}
            </Typography>
          </Tooltip>
          <Typography sx={{ color: theme.palette.shades.lightDark }} variant="textXS">
            {topRighField?.column}
          </Typography>
        </Grid>
      )}
    </Grid>

    <Grid item xs={12} padding="12px">
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-around"
        alignItems="center"
        width="100%"
        paddingY={0.5}
        paddingX={1}
        borderRadius="5px"
        sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}
      >
        <Box>
          <Tooltip title={gem.color}>
            <Typography
              variant="textBase500"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: '100px',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {renderColorDot(gem.color)}
              {gem.color}
            </Typography>
          </Tooltip>
          <Typography variant="textXS" sx={{ color: theme.palette.shades.lightDark }}>
            Primary Color
          </Typography>
        </Box>
        <Box>
          <Tooltip title={gem.color}>
            <Typography
              variant="textBase500"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: '100px',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {renderColorDot(gem.secondaryColor)}
              {gem.secondaryColor ?? 'NA'}
            </Typography>
          </Tooltip>
          <Typography variant="textXS" sx={{ color: theme.palette.shades.lightDark }}>
            Secondary Color
          </Typography>
        </Box>
        <Box>
          <Tooltip title={gem.color}>
            <Typography
              variant="textBase500"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: '100px',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {gem.intensity ?? 'NA'}
            </Typography>
          </Tooltip>
          <Typography variant="textXS" sx={{ color: theme.palette.shades.lightDark }}>
            Intensity
          </Typography>
        </Box>
      </Box>
    </Grid>

    <Grid paddingX="12px" container justifyContent="flex-start" minWidth="150px">
      <Grid item gap={2}>
        <Tooltip title={gem.specialDescriptor || 'N/A'}>
          <Box
            sx={{
              padding: 0.5,
              borderRadius: '5px',
              border: `1px solid ${theme.palette.shades.grayBorder}`,
              backgroundColor: 'rgba(51, 72, 255, 0.04)',
              maxWidth: '100%',
            }}
          >
            <Typography
              variant="textBase500"
              color="black.dark"
              sx={{
                display: 'block',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflow: 'hidden',
                maxWidth: '100%',
                textOverflow: 'ellipsis',
              }}
            >
              {gem.specialDescriptor || 'N/A'}
            </Typography>
          </Box>
        </Tooltip>
        <Typography sx={{ color: theme.palette.shades.lightDark }} variant="textXS">
          Special Color Descriptor
        </Typography>
      </Grid>
    </Grid>

    <Grid padding="12px" container rowGap={1}>
      {columns.map((column, i) => (
        <Grid item key={column} xs={6}>
          <Tooltip title={gem[columnValues[i]] || 'N/A'}>
            <Typography
              variant="textBase500"
              color="black.dark"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-all',
              }}
            >
              {gem[columnValues[i]] || 'N/A'}
            </Typography>
          </Tooltip>
          <Typography sx={{ color: theme.palette.shades.lightDark }} variant="textXS">
            {column}
          </Typography>
        </Grid>
      ))}
    </Grid>
    <Grid
      item
      sx={{
        padding: '12px',
        margin: 0,
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 5,
      }}
    >
      <Box display="flex" gap="8px" justifyItems="center" alignItems="center">
        <Grid>
          <CustomButton variant="outlined" onClick={handleClickFullDetailButton}>
            View Details
          </CustomButton>
        </Grid>
        {!!gem.organizationId && (
          <Grid>
            <IconButton
              disabled={!handleEditGem}
              sx={{
                border: `1px solid ${
                  handleEditGem ? theme.palette.shades.lightBlueColor : theme.palette.action.disabledBackground
                }`,
                borderRadius: '8px',
                padding: 1,
              }}
              size="small"
              onClick={handleEditGem}
              role="button"
              aria-label="edit-gem"
              data-testid="edit-gem"
            >
              <Box component="img" src={handleEditGem ? PencilEditSvg : PencilEditDisabledSvg} />
            </IconButton>
          </Grid>
        )}
      </Box>
      <Grid justifyContent="end" display="flex">
        <Box>
          {showPublicIcon && (
            <IconButton onClick={handleTogglePublic} size="small" role="button" aria-label="toggle-public">
              {gem.isPublic ? (
                <Box data-testid="btn_favorit" component="img" src={PorolisGemBankSvgBold} />
              ) : (
                <Box data-testid="btn_favorit" component="img" src={PorolisGemBankSvg} />
              )}
            </IconButton>
          )}

          <IconButton size="small" onClick={handleFavourite} role="button" aria-label="toggle-favourite">
            {gem.isFavourite ? (
              <Box data-testid="btn_favorit" component="img" src={FavouriteBold} />
            ) : (
              <Box data-testid="btn_favorit" component="img" src={FavouriteSvg} />
            )}
          </IconButton>
        </Box>
      </Grid>
    </Grid>
  </CustomGridContainer>
);

export const GridTileLoading = () => (
  <CustomGridContainer container gap={1} data-testid="grid-tile-loading">
    <Grid container justifyContent="space-between" mb="16px" data-testid="skeleton">
      <Grid item>
        <Skeleton variant="text" width={100} height={25} />
        <Skeleton variant="text" width={50} height={15} />
      </Grid>

      <Grid
        item
        sx={{
          padding: 0,
          margin: 0,
        }}
      >
        <Skeleton variant="rectangular" width={75} height={35} />
      </Grid>
    </Grid>

    <Grid item display="flex" justifyContent="center" xs={12} sm={12}>
      <Skeleton variant="rectangular" width="100px" height="100px" />
    </Grid>

    <Grid container rowGap={1} mt="16px">
      {[...Array(8)].map((_) => (
        <Grid item key={uuidv4()} xs={12} md={4} gap={2}>
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={100} height={25} />
        </Grid>
      ))}
    </Grid>
  </CustomGridContainer>
);
export default GridTile;
